// src/components/gps/GPSServerSettings.js
import React, { useState, useEffect } from 'react';
import {
  testConnection, saveCredentials,
  clearCredentials, loadSavedCredentials, loadCredentialsFromBackend, TraccarConfig, fetchTraccarDevices,
} from '../../services/traccar';
import { Card, Button, FormField, Input, Badge } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';

export default function TraccarSettings({ onConnected }) {
  const { isMobile } = useResponsive();
  const [serverUrl, setServerUrl] = useState('https://gps.pixelsouth.com');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [status, setStatus]       = useState('idle'); // idle|testing|connected|error
  const [errorMsg, setErrorMsg]   = useState('');
  const [serverInfo, setServerInfo]     = useState(null);
  const [detectedBase, setDetectedBase] = useState('');
  const [deviceCount, setDeviceCount]   = useState(null);
  const [showDebug, setShowDebug]       = useState(false);
  const [debugLog, setDebugLog]         = useState([]);

  useEffect(() => {
    (async () => {
      // Prefer the backend's shared config (the same one every other user
      // now bootstraps from) so this form reflects the connection that's
      // actually in effect, rather than possibly-stale local browser data.
      await loadCredentialsFromBackend();
      if (TraccarConfig.isConfigured()) {
        const creds = TraccarConfig.getCredentials();
        setServerUrl(creds.serverUrl);
        setEmail(creds.email);
        setPassword(creds.password);
        doConnect(creds.serverUrl, creds.email, creds.password, true);
        return;
      }
      // Nothing configured on the backend yet — fall back to whatever
      // this specific browser has saved from before.
      const saved = loadSavedCredentials();
      if (saved) {
        setServerUrl(saved.serverUrl || 'https://gps.pixelsouth.com');
        setEmail(saved.email);
        setPassword(saved.password);
        doConnect(saved.serverUrl || 'https://gps.pixelsouth.com', saved.email, saved.password, true);
      }
    })();
  }, []); // eslint-disable-line

  function log(msg) {
    setDebugLog(prev => [...prev.slice(-19), `${new Date().toLocaleTimeString()} — ${msg}`]);
  }

  async function doConnect(url, em, pw, silent = false) {
    if (!silent) setStatus('testing');
    setErrorMsg('');
    log(`Connecting to ${url}...`);
    log(`Detecting API path...`);

    const result = await testConnection(url, em, pw);

    if (result.success) {
      saveCredentials(url, em, pw);
      setStatus('connected');
      setServerInfo(result.user);
      setDetectedBase(result.detectedBase || '');
      log(`✅ Connected! API base: ${result.detectedBase}`);
      log(`Account: ${result.user?.name} (${result.user?.email})`);
      try {
        const devices = await fetchTraccarDevices();
        setDeviceCount(devices.length);
        log(`Found ${devices.length} device(s)`);
      } catch (e) {
        log(`Could not fetch devices: ${e.message}`);
      }
      onConnected?.(true);
    } else {
      if (!silent) setStatus('error');
      else setStatus('idle');
      setErrorMsg(result.error || 'Unknown error');
      log(`❌ Error: ${result.error}`);
      onConnected?.(false);
    }
  }

  function handleConnect() {
    if (!serverUrl || !email || !password) return;
    doConnect(serverUrl, email, password);
  }

  function handleDisconnect() {
    clearCredentials();
    setStatus('idle');
    setServerInfo(null);
    setDetectedBase('');
    setDeviceCount(null);
    setEmail('');
    setPassword('');
    setDebugLog([]);
    onConnected?.(false);
  }

  const isConnected = status === 'connected';
  const isTesting   = status === 'testing';

  return (
    <Card>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: isConnected ? 'rgba(16,185,129,0.12)' : 'rgba(139,92,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
            📡
          </div>
          <div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, color: 'var(--textPrimary)' }}>GPS Server Connection</div>
            <div style={{ fontSize: 12, color: 'var(--textMuted)', marginTop: 1 }}>
              Device IP: <span style={{ color: '#f97316', fontFamily: 'monospace' }}>187.77.137.252:5015</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge color={isConnected ? '#10b981' : isTesting ? '#f59e0b' : '#6b7280'}>
            {isConnected ? '● Connected' : isTesting ? '◌ Connecting...' : '○ Disconnected'}
          </Badge>
          <button
            onClick={() => setShowDebug(p => !p)}
            style={{ background: 'none', border: '1px solid var(--borderInput)', borderRadius: 6, color: 'var(--textFaint)', cursor: 'pointer', padding: '3px 8px', fontSize: 10 }}
          >
            {showDebug ? 'Hide Log' : 'Show Log'}
          </button>
        </div>
      </div>

      <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 9, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: 'var(--textSecondary)' }}>
        ℹ️ Connecting here saves credentials for your own browser only. For every user — including client accounts, who can't see this page at all — to automatically get a working connection, the same GPS server details also need to be set in your backend's <code style={{ background: 'var(--bgTag)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>.env</code> file (<code style={{ background: 'var(--bgTag)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>TRACCAR_URL</code>, <code style={{ background: 'var(--bgTag)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>TRACCAR_EMAIL</code>, <code style={{ background: 'var(--bgTag)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>TRACCAR_PASSWORD</code>) — the same values used for the background travel-history tracker.
      </div>

      {/* Connected info */}
      {isConnected && serverInfo && (
        <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
            <div>
              <div style={lbl}>Account</div>
              <div style={val}>{serverInfo.name || serverInfo.email}</div>
            </div>
            <div>
              <div style={lbl}>API Endpoint</div>
              <div style={{ ...val, fontFamily: 'monospace', fontSize: 11, color: '#10b981' }}>{detectedBase || '—'}</div>
            </div>
            <div>
              <div style={lbl}>Registered Devices</div>
              <div style={{ ...val, color: '#10b981' }}>{deviceCount ?? '—'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Device connection reference */}
      <div style={{ background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Physical Device Config</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
          <div><div style={lbl}>Server IP</div><div style={{ ...val, fontFamily: 'monospace', color: '#f97316' }}>187.77.137.252</div></div>
          <div><div style={lbl}>Port (GT06/Concox)</div><div style={{ ...val, fontFamily: 'monospace', color: '#f97316' }}>5015</div></div>
          <div><div style={lbl}>Protocol</div><div style={val}>GT06 / Concox / H02</div></div>
        </div>
      </div>

      {/* Credentials form */}
      {!isConnected ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            GPS Server Login
          </div>

          <FormField label="GPS Server URL">
            <Input value={serverUrl} onChange={setServerUrl} placeholder="https://gps.pixelsouth.com" />
          </FormField>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <FormField label="Email">
              <Input value={email} onChange={setEmail} placeholder="admin@example.com" type="email" />
            </FormField>
            <FormField label="Password">
              <Input value={password} onChange={setPassword} placeholder="••••••••" type="password" />
            </FormField>
          </div>

          {status === 'error' && (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 13, color: '#f87171', fontWeight: 600, marginBottom: 6 }}>⚠ Connection Failed</div>
              <div style={{ fontSize: 12, color: '#fca5a5' }}>{errorMsg}</div>
              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--textMuted)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--textPrimary)' }}>Things to check:</strong><br />
                1. Open <a href={serverUrl} target="_blank" rel="noreferrer" style={{ color: '#f97316' }}>{serverUrl}</a> in your browser — can you log in?<br />
                2. Verify your email and password are correct<br />
                3. Click <strong>Show Log</strong> above to see detailed connection info
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleConnect} disabled={isTesting || !serverUrl || !email || !password}>
              {isTesting ? '⏳ Connecting...' : '🔌 Connect GPS Server'}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="danger" onClick={handleDisconnect}>Disconnect</Button>
        </div>
      )}

      {/* Debug log */}
      {showDebug && (
        <div style={{ marginTop: 16, background: 'var(--bgApp)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            Connection Log
          </div>
          {debugLog.length === 0
            ? <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>No activity yet. Click Connect to start.</div>
            : debugLog.map((line, i) => (
                <div key={i} style={{ fontSize: 11, fontFamily: 'monospace', color: line.includes('✅') ? '#34d399' : line.includes('❌') ? '#f87171' : 'var(--textMuted)', marginBottom: 3 }}>
                  {line}
                </div>
              ))
          }
        </div>
      )}
    </Card>
  );
}

const lbl  = { fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 };
const val  = { fontSize: 13, color: 'var(--textSecondary)', fontWeight: 500 };
